import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const http = axios.create({ baseURL: API });

const nf0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const cur = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });

export const fmtNum = (v) => (v === null || v === undefined ? "n.d." : nf0.format(v));
export const fmtMXN = (v) => (v === null || v === undefined ? "n.d." : `${cur.format(v)} MXN`);
export const fmtPct = (v, d = 1) => (v === null || v === undefined ? "n.d." : `${(v * 100).toFixed(d)}%`);
export const fmtM = (v) => (v === null || v === undefined ? "n.d." : `MXN ${(v / 1e6).toFixed(2)}M`);

export const TYPE_LABEL = {
  cfdi_zip: "ZIP CFDI (CIEC)",
  cfdi_xml: "CFDI XML",
  ai_extraction: "Extracción IA",
  memo: "Credit Memo",
};

export const STATUS_LABEL = { queued: "En cola", running: "Procesando", done: "Completado", error: "Error" };

const TOKEN_KEY = "trf_token";
const REFRESH_KEY = "trf_refresh";
export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  set: (t, r) => { localStorage.setItem(TOKEN_KEY, t); if (r) localStorage.setItem(REFRESH_KEY, r); },
  clear: () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(REFRESH_KEY); },
};

// Actividad real del usuario (inactividad CNBV): interacción reciente o una operación larga en curso.
export const actividad = { ultima: Date.now(), enCurso: 0 };
export const marcarActividad = () => { actividad.ultima = Date.now(); };
export const usuarioActivo = () => actividad.enCurso > 0 || Date.now() - actividad.ultima < 60000;

http.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t && !esPublica() && !/^\/?alta-ref\//.test(cfg.url || "")) cfg.headers.Authorization = `Bearer ${t}`;
  if (usuarioActivo()) cfg.headers["X-User-Active"] = "1";
  if ((cfg.method || "get") !== "get") { actividad.enCurso += 1; cfg._busy = true; }
  return cfg;
});
const fin = (cfg) => { if (cfg?._busy) { actividad.enCurso = Math.max(0, actividad.enCurso - 1); actividad.ultima = Date.now(); cfg._busy = false; } };

let refreshing = null;
const refrescar = () => {
  refreshing = refreshing || axios.post(`${API}/auth/refresh`, { refresh_token: tokenStore.refresh() })
    .then((r) => { tokenStore.set(r.data.access_token, r.data.refresh_token); return r.data.access_token; })
    .finally(() => { refreshing = null; });
  return refreshing;
};
// Páginas públicas (sin sesión): nunca envían token ni redirigen a /login.
export const esPublica = () => /^\/(ref|alta-referenciador|solicitud)(\/|$)/i.test(window.location.pathname);
export const cerrarSesionLocal = (motivo) => {
  tokenStore.clear();
  if (esPublica()) return;
  if (motivo) sessionStorage.setItem("logout_motivo", motivo);
  if (window.location.pathname !== "/login") window.location.assign("/login");
};
http.interceptors.response.use((r) => { fin(r.config); return r; }, async (err) => {
  fin(err.config);
  const cfg = err.config || {};
  // Errores de descargas (responseType blob): convierte el cuerpo JSON en objeto para mostrar el detalle real.
  const b = err.response?.data;
  if (b instanceof Blob && /json/.test(b.type || "")) { try { err.response.data = JSON.parse(await b.text()); } catch { /* cuerpo no JSON */ } }
  // 401 de confirmación con contraseña (p. ej. firmar un alta) no es sesión expirada: no cerrar sesión.
  if (err.response?.status === 401 && !cfg.url?.includes("/auth/login") && !/\/altas\/[^/]+\/firmar$/.test(cfg.url || "")) {
    const d = err.response?.data?.detail || "";
    if (d === "Sesión expirada" && tokenStore.refresh() && !cfg._retry) {
      try {
        const t = await refrescar();
        return http({ ...cfg, _retry: true, headers: { ...cfg.headers, Authorization: `Bearer ${t}` } });
      } catch { /* cae a cierre de sesión */ }
    }
    cerrarSesionLocal(typeof d === "string" ? d : "");
  }
  return Promise.reject(err);
});

export const errMsg = (e, fallback = "Ocurrió un error. Intenta de nuevo.") => {
  const d = e?.response?.data?.detail;
  if (d == null) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

export const EXP_STATUS = {
  borrador: ["Borrador", "bg-slate-100 text-slate-600 border-slate-300"],
  en_revision: ["En revisión", "bg-amber-50 text-amber-700 border-amber-200"],
  aprobado: ["Aprobado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  rechazado: ["Rechazado", "bg-rose-50 text-rose-700 border-rose-200"],
};

const esIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const NO_ARCHIVO = /^(application\/json|text\/html)/;

// Guarda un Blob como archivo: valida el tipo, ancla en el DOM, clic, revoca tarde. iOS Safari: abre en pestaña nueva.
export const saveBlob = (data, filename) => {
  if (!(data instanceof Blob) || !data.size || NO_ARCHIVO.test(data.type || "")) throw new Error("El servidor no devolvió un archivo válido. Intenta de nuevo.");
  const url = URL.createObjectURL(data);
  if (esIOS()) {
    const w = window.open(url, "_blank");
    if (!w) window.location.assign(url);
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Abre un PDF en una pestaña; si el navegador bloquea la ventana emergente, lo descarga.
export const openBlob = (data, filename, hash = "") => {
  if (!(data instanceof Blob) || NO_ARCHIVO.test(data.type || "")) throw new Error("El servidor no devolvió un archivo válido.");
  const url = URL.createObjectURL(data);
  const w = window.open(`${url}${hash}`, "_blank");
  if (!w) saveBlob(data, filename);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Descarga autenticada: nunca rechaza; muestra el error y devuelve false.
export const downloadBlob = async (url, filename, method = "get", body) => {
  try {
    const r = method === "post" ? await http.post(url, body, { responseType: "blob" }) : await http.get(url, { responseType: "blob" });
    return saveBlob(r.data, filename);
  } catch (e) {
    toast.error(`No se pudo descargar «${filename}»: ${errMsg(e, e?.message || "error de red")}`);
    return false;
  }
};
